import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { TieredMenu } from 'primereact/tieredmenu';
import type { MenuItem } from 'primereact/menuitem';
import { confirmDialog } from 'primereact/confirmdialog';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import { fijarArchivo, MAX_TIENDAS_EXCEL, MetasApiError, MetasColaboradorService } from '../services/metasColaborador.service';
import type {
  CopiaOmitida,
  FilaVista,
  ModoReparto,
  RepartoElegibles,
  RepartoPropuesta,
  MiAlcance,
  ResultadoImportacion,
  SuplenteFila,
  SuplentesDeTienda,
  TiendaRef,
} from '../types/metas.types';
import {
  ajusteDelGerente,
  aplicarCopia,
  coincideFiltroCaptura,
  conteosCaptura,
  CopiaSaltada,
  filtrosCaptura,
  FiltroCaptura,
  listaDeNombres,
  pasoPorGerente,
  propuestasQuePisan,
  textoFaltan,
  tiendasDelSelector,
} from '../utils/captura';
import { minimoDeAlcance } from '../utils/catalogo';
import { MENSAJE_ENVIADA_SOLO_LECTURA, textoEstadoCaptura } from '../utils/estado';
import { capturaDeTienda } from '../utils/flujo';
import { nombrePropioSiMayusculas } from '../utils/autorizacion';
import { CODIGO_META_A_BODEGUERO, esMetaABodeguero, TEXTO_META_A_BODEGUERO } from '../utils/bodeguero';
import { CODIGO_META_A_CASA, esFilaCasa, esMetaACasa, TEXTO_META_A_CASA, TITULO_AGREGAR_CASA } from '../utils/casa';
import { instanteMx, momentoMx, pesos, pesosEnteros } from '../utils/formato';
import { avisosConMetaTienda } from '../utils/kpis';
import { aplicarReparto, esPropuestaIguales, esRepartoElegibles, hayQueRepartir, porAsignarDe } from '../utils/reparto';
import { esPendiente } from '../utils/totales';
import { pendientesPorFila } from '../utils/pendientes';
import { guardarArchivo } from '../utils/descarga';
import { GRUPO_CONFIRMAR } from '../utils/confirmar';
import type { TiendaParam } from '../utils/rutas';
import { aErrorPantalla, llaveTienda, MENSAJE_TIENDA_DEL_ENLACE, useCapturaTienda } from '../hooks/useCapturaTienda';
import { useAutoguardado } from '../hooks/useAutoguardado';
import { AvisosLista } from './AvisosLista';
import { BannerPorResolver, ChipsColaboradores, FiltroColaboradores } from './BannerPorResolver';
import { BannerPendientesCatalogo } from './BannerPendientesCatalogo';
import { ColaboradoresTabla } from './ColaboradoresTabla';
import { DevolverDialog } from './DevolverDialog';
import { ElegirRepartoDialog } from './ElegirRepartoDialog';
import { ImportarExcelDialog } from './ImportarExcelDialog';
import { ResumenCaptura } from './ResumenCaptura';
import { ObjetivoSuplente, SuplenteDialog } from './SuplenteDialog';
import { ReabrirControl } from './ReabrirControl';
import { RepartirDialog } from './RepartirDialog';
import { BarraAccionesFija } from './ui/BarraAccionesFija';
import { Chip } from './ui/Chip';
import { CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_BOTON_TEXTO, CLASE_OVERLAY, CLASE_PANEL, cx } from './ui/clases';
import { OpcionTienda, SelectorTienda } from './ui/SelectorTienda';

export { ORIGEN_CAPTURA } from '../hooks/useCapturaTienda';

type AvisoToast = (severidad: 'success' | 'warn' | 'error', resumen: string, detalle?: string) => void;

interface Props {
  alcance: MiAlcance;
  lunes: string;
  onAviso: AvisoToast;
  /** La pantalla lo usa para no cambiar de semana sin guardar lo pendiente. */
  onCambiosSinGuardar?: (sucio: boolean) => void;
  /** Mientras guarda o envía: la pantalla bloquea el selector de semana. */
  onOcupado?: (ocupado: boolean) => void;
  /**
   * Se propuso, retiró o resolvió algo del catálogo (suplentes): la pantalla
   * recarga «Pendientes de catálogo (N)» y los contadores de Autorización.
   */
  onCambioCatalogo?: () => void;
  /** La tienda del enlace (`?tienda=VAZ-1`). Se usa UNA vez, con la primera lista. */
  tiendaInicial?: TiendaParam | null;
  /** Cada vez que se abre una tienda (o `null`: no quedó ninguna): la pantalla lo escribe en el enlace. */
  onTienda?: (t: TiendaParam | null) => void;
  /**
   * La pantalla deja aquí «guarda lo pendiente ya» para llamarlo antes de
   * cambiar de semana: `true` = no queda nada sin guardar; `false` = no se pudo
   * (y entonces pregunta si se descarta).
   */
  guardarPendienteRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  /** Espera del autoguardado (2 s). Las pruebas la acortan o la fijan. */
  demoraAutoguardado?: number;
  /**
   * «Ver captura completa» desde Autorización: abre esa tienda con la misma
   * protección de cambios sin guardar que el selector. Es un objeto NUEVO en
   * cada pedido (`n`), así que pedir dos veces la misma tienda también cuenta.
   */
  abrirTienda?: (TiendaParam & { n: number }) | null;
  /**
   * Cambia cuando Autorización resolvió algo (autorizar, rechazar, reabrir, el
   * lote): la Captura relee su lista y, si no hay nada sin guardar, la tienda
   * abierta. Sin esto seguía mostrando «Enviada» de sólo lectura tras un rechazo.
   */
  recarga?: number;
  /** Se envió, se devolvió al gerente o se reabrió una tienda desde aquí: Autorización recarga su lista y el contador. */
  onCambioEstado?: () => void;
  /** «Ver en Pendientes de catálogo» desde el banner de la tienda (fase 4): con la tienda, para abrir la lista filtrada. */
  onVerPendientes?: (t: TiendaParam) => void;
}

/** Lo que acepta «Subir Excel…»: la plantilla es `.xlsx`; un `.xls` o `.csv` la API lo rechaza. */
const ACEPTA_EXCEL = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Lo que dejó «Copiar semana anterior» sin copiar, para decirlo con nombre y razón. */
interface ResultadoCopia {
  numero: number;
  omitidas: CopiaOmitida[];
  saltadas: CopiaSaltada[];
  respetadas: number;
}

const Icono: React.FC<{ d: string; className?: string }> = ({ d, className }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d={d} />
  </svg>
);
const ICONO_COPIAR = 'M8 8h12v12H8zM16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3';
const ICONO_EXCEL = 'M14 3H6v18h12V7zM14 3v4h4M9.5 12l5 5M14.5 12l-5 5';
const ICONO_GUARDAR = 'M5 4h11l3 3v13H5zM8 4v5h7V4M8 20v-6h8v6';
const ICONO_ENVIAR = 'M21 3L10 14M21 3l-7 18-4-7-7-4z';
const Girando = () => <i className="pi pi-spin pi-spinner" aria-hidden="true" />;

/**
 * Captura de metas de UNA tienda (diseño `Main` y `MovilCaptura`, fase 2b).
 *
 * La lógica (carga, guardar, enviar, versión, 409/422/503) vive en
 * `useCapturaTienda`; el guardado automático en `useAutoguardado`. Aquí se pinta:
 *
 * - barra de la tienda: selector con anterior/siguiente, «Copiar semana
 *   anterior» (propuesta de la API; nunca pisa lo escrito sin preguntar) y el
 *   menú «Excel» (bajar plantilla, subir Excel con vista previa);
 * - arriba, el comentario del rechazo o el aviso de sólo lectura (y «Reabrir»);
 * - el resumen (cabecera, pasos y los cuatro indicadores);
 * - el banner «Por resolver antes de enviar» con los chips, y la tabla (o las
 *   tarjetas en teléfono, las pendientes primero);
 * - lo que el diseño no muestra y se conserva: advertencias para el regional y
 *   la nota para el regional. «Vendieron aquí sin ser de la plantilla» se quitó
 *   el 21-oct-2026 (decisión del usuario): la API lo sigue calculando —para
 *   casar lo ya guardado— y las filas guardadas desde ahí conservan su
 *   distintivo «Vendió aquí». «Condiciones de la tienda» también (misma fecha):
 *   transporte y bodeguero fijo se capturan POR PERSONA en la tabla;
 * - el pie fijo: por qué no se puede enviar, cuándo se guardó, Guardar y Enviar.
 *
 * EL GERENTE (fase B, 21-oct-2026, diseño `Supervisor-revision`): el gerente
 * captura desde la app de la tienda y se la manda al supervisor («con el
 * supervisor»). Aquí, el supervisor ve chips por estado sobre su lista de
 * tiendas (te la enviaron, en captura del gerente, enviadas al regional,
 * devueltas), quién y cuándo se la mandó con su nota, sus ajustes frente a lo
 * que puso el gerente («El gerente puso $X»), y decide: «Guardar ajustes»,
 * «Devolver al gerente…» (con comentario obligatorio) o «Enviar al regional».
 * Sigue pudiendo capturar él mismo la tienda cuyo gerente no capturó.
 * Todo eso, sólo con la captura de la tienda encendida (`capturaTienda` de
 * `mi-alcance`, por entorno). Por omisión está APAGADA (22-oct-2026, decisión
 * del usuario: «de momento seguir dejando la tarea de capturar la meta a los
 * supervisores»): chips sin el gerente («En captura»), los tres pasos del
 * supervisor y sin «Devolver al gerente». Lo que quedó de cuando estaba
 * encendida («Capturó el gerente», «El gerente puso $X») se sigue diciendo:
 * es lo que pasó.
 *
 * EXCEL (fase 2): «todas mis tiendas» y «Subir Excel…» sirven sin abrir
 * ninguna tienda. Subir cambia lo GUARDADO y al terminar la tienda se recarga,
 * así que con cambios sin guardar se pregunta antes.
 *
 * CATÁLOGO (fase 3): los suplentes NO son parte de la captura: no pasan por
 * guardar, valen por tienda y se proponen aunque la semana ya no se pueda
 * editar. Tras proponer, retirar o certificar un suplente se corrige SÓLO el
 * programa de esa persona en la copia local y en la del servidor.
 */
export const CapturaTab: React.FC<Props> = ({
  alcance,
  lunes,
  onAviso,
  onCambiosSinGuardar,
  onOcupado,
  onCambioCatalogo,
  tiendaInicial,
  onTienda,
  guardarPendienteRef,
  demoraAutoguardado,
  abrirTienda,
  recarga,
  onCambioEstado,
  onVerPendientes,
}) => {
  // La disposición del teléfono hasta 1023 px, como las tarjetas de
  // `ColaboradoresTabla` (auditoría del 22-oct-2026: a 768 la tabla compacta se rompía).
  const esMovil = useEsMovil(1023);
  // ¿Captura la tienda (el gerente) o sólo el supervisor? Lo dice el entorno (22-oct-2026).
  const capturaTienda = capturaDeTienda(alcance);
  const c = useCapturaTienda({ lunes, onAviso, onCambiosSinGuardar, tiendaInicial, onTienda, onEnviada: onCambioEstado });
  // Lo último del hook, para efectos que no deben volver a correr con cada render.
  const cRef = useRef(c);
  cRef.current = c;
  useEffect(() => {
    if (!recarga) return;
    void cRef.current.cargarLista();
    if (!cRef.current.estaSucio()) void cRef.current.cargarVista();
  }, [recarga]);
  const {
    lista,
    vista,
    filas,
    sel,
    setSel,
    trabajando,
    soloLectura,
    estadoSoloLectura,
    plantillaIncompleta,
    totales,
    errores,
    sucio,
    porEnviar,
    motivoNoCapturar,
  } = c;
  // Además de sólo lectura: mientras carga otra tienda (la de pantalla es la que
  // se deja: lo escrito se perdía o su guardado pintaba la vieja encima de la
  // nueva) y mientras se envía (lo tecleado no viajaba y el envío lo borraba).
  const bloqueada = soloLectura || c.cargandoVista || c.enviando;
  // La vista en pantalla es de OTRA tienda que la elegida (la nueva aún no
  // llega). Mientras tanto —o mientras se recarga— no se abre nada de una fila:
  // el menú ⋮ abría «Suplente en desarrollo…» de la tienda que se dejaba.
  const vistaDeOtraTienda = !!vista && sel !== llaveTienda(vista.tienda.chain, vista.tienda.num);
  const desfasada = c.cargandoVista || vistaDeOtraTienda;

  // «Se le está colocando meta al bodeguero» (23-oct-2026, decisión del usuario):
  // EN VIVO, con lo que hay en pantalla, sin esperar al guardado. No bloquea
  // guardar ni enviar; el regional lo ve en su revisión.
  const bodeguerosConMeta = useMemo(() => filas.filter(esMetaABodeguero), [filas]);
  const textoBodeguerosConMeta = bodeguerosConMeta.length
    ? `${TEXTO_META_A_BODEGUERO}: ${listaDeNombres(
        bodeguerosConMeta.map((f) => `${f.nombre} (${pesosEnteros(f.metaMonto)})`),
        bodeguerosConMeta.length,
      )}.`
    : null;
  // «Se le está colocando meta a la Casa» (ronda F, 23-oct-2026, decisión del
  // usuario): igual que el del bodeguero, en vivo y sin bloquear nada.
  const casaConMeta = useMemo(() => filas.find((f) => esMetaACasa(f)) ?? null, [filas]);
  const textoCasaConMeta = casaConMeta ? `${TEXTO_META_A_CASA}: ${casaConMeta.nombre} (${pesosEnteros(casaConMeta.metaMonto)}).` : null;

  // «Copiar semana anterior» en camino (se declara aquí: apaga el autoguardado).
  const [copiando, setCopiando] = useState(false);
  // Pidiendo una propuesta de «Repartir» (según venta promedio o en partes
  // iguales, ronda F): también lo apaga. Dice cuál, para el ícono de su enlace.
  const [repartiendo, setRepartiendo] = useState<ModoReparto | null>(null);

  // ------------------------------------------------------------ autoguardado
  const auto = useAutoguardado({
    // Apagado mientras se envía, se copia la semana anterior o carga otra
    // tienda: un guardado a mitad de esas peticiones cambiaba la versión (falso
    // «otra pestaña u otra persona» al copiar, 409 al enviar) o guardaba la
    // tienda que se estaba dejando.
    habilitado: c.puedeAutoguardar && !c.enviando && !copiando && !repartiendo && !c.cargandoVista,
    sucio,
    firma: `${JSON.stringify(
      filas.map((f) => [f.key, f.metaMonto, f.sinMetaMotivo, f.sinMetaDetalle, f.apoyo, f.transporte, f.bodegueroFijo, f.diasIncapacidad]),
    )}|${c.nota}`,
    guardar: async () => !!(await c.guardar({ silencioso: true, auto: true })),
    pendienteAhora: () => ({ habilitado: c.puedeAutoguardarRef.current, sucio: c.estaSucio() }),
    demoraMs: demoraAutoguardado,
  });

  /** Guarda lo pendiente YA (antes de cambiar de tienda o de semana). */
  const guardarPendiente = useCallback(async (): Promise<boolean> => {
    if (!c.estaSucio()) return true;
    return auto.guardarYa();
  }, [auto, c]);
  useEffect(() => {
    if (!guardarPendienteRef) return undefined;
    guardarPendienteRef.current = guardarPendiente;
    return () => {
      guardarPendienteRef.current = null;
    };
  }, [guardarPendienteRef, guardarPendiente]);

  // ------------------------------------------------------------ filtro y orden
  // «Por resolver» fija QUIÉNES se ven al pulsarlo: si la fila desapareciera al
  // escribirle la meta, se iría de debajo del dedo a media captura.
  const [soloPendientes, setSoloPendientes] = useState<Set<string> | null>(null);
  const [resultadoCopia, setResultadoCopia] = useState<ResultadoCopia | null>(null);
  const llaveVista = vista ? `${vista.tienda.chain}|${vista.tienda.num}|${vista.semana.lunes}` : null;
  // La vista previa de «Repartir» es de UNA tienda y semana.
  const [reparto, setReparto] = useState<{ propuesta: RepartoPropuesta; llave: string; modo: ModoReparto } | null>(null);
  // «Repartir en partes iguales» (ronda F): quién puede recibir (paso 1), lo que
  // se eligió (para «Cambiar selección») y si el paso 1 está a la vista.
  const [iguales, setIguales] = useState<{
    datos: RepartoElegibles;
    llave: string;
    marcadas: string[] | null;
    abierto: boolean;
    error: string | null;
  } | null>(null);
  useEffect(() => {
    setSoloPendientes(null);
    setResultadoCopia(null);
    setReparto(null);
    setIguales(null);
  }, [llaveVista]);

  const pendientesAhora = useMemo(() => filas.filter((f) => esPendiente(f)), [filas]);
  const filtro: FiltroColaboradores = soloPendientes ? 'pendientes' : 'todos';
  const alFiltrar = (f: FiltroColaboradores) =>
    setSoloPendientes(f === 'pendientes' ? new Set(pendientesAhora.map((x) => x.key)) : null);
  useEffect(() => {
    // Ya no queda nadie de los que se filtraron por resolver: se vuelve a todos.
    if (soloPendientes && !soloPendientes.size) setSoloPendientes(null);
  }, [soloPendientes]);

  // El orden de la tabla y de las tarjetas se CONGELA: se calcula al abrir la
  // tienda (o la semana) y al cambiar de filtro, no con cada vista adoptada. La
  // respuesta de cada autoguardado llega en el orden del servidor (lo guardado,
  // por nombre) y la fila recién llenada saltaba de lugar —en el teléfono, al
  // dejar de ser pendiente, su tarjeta se iba al final, de debajo del dedo (AG-1);
  // a 1440, al elegir un motivo la fila se iba al último renglón—. En el
  // teléfono, además, las pendientes van primero. Quien se agrega después, al final.
  const ordenCongelado = useMemo(() => {
    const base = c.filasRef.current;
    const lista = esMovil ? [...base.filter((f) => esPendiente(f)), ...base.filter((f) => !esPendiente(f))] : base;
    return new Map(lista.map((f, i) => [f.key, i]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a propósito: sólo al abrir la tienda o la semana, o al cambiar de filtro
  }, [llaveVista, filtro, esMovil]);
  const filasVisibles = useMemo(() => {
    const vis = soloPendientes ? filas.filter((f) => soloPendientes.has(f.key)) : filas;
    const pos = (f: FilaVista) => ordenCongelado.get(f.key) ?? Number.MAX_SAFE_INTEGER;
    return vis
      .map((f, i) => ({ f, i }))
      .sort((a, b) => pos(a.f) - pos(b.f) || a.i - b.i)
      .map((x) => x.f);
  }, [filas, soloPendientes, ordenCongelado]);

  // ------------------------------------------------------------ suplentes (fase 3)
  // Los programas de ESTA tienda (con permisos por programa). No dependen de la
  // semana, pero se piden otra vez CADA VEZ que se adopta una vista: con la
  // lista de antes se negaba «Certificar» a un suplente ya autorizado.
  const [suplentesTienda, setSuplentesTienda] = useState<SuplentesDeTienda | null>(null);
  const [suplenteDe, setSuplenteDe] = useState<ObjetivoSuplente | null>(null);
  const pedidoSuplentes = useRef(0);
  const vistaDelDialogo = useRef(0);
  const tiendaDeSuplentes = useRef<string | null>(null);
  const cadena = vista?.tienda.chain;
  const numero = vista?.tienda.num;

  const cargarSuplentes = useCallback(async (chain: string, num: number) => {
    const n = ++pedidoSuplentes.current;
    try {
      const s = await MetasColaboradorService.suplentes(chain, num);
      if (n === pedidoSuplentes.current) setSuplentesTienda(s);
    } catch {
      // Sin la lista, el diálogo sigue sirviendo: la API revisa todo al guardar.
      if (n === pedidoSuplentes.current) setSuplentesTienda(null);
    }
  }, []);

  useEffect(() => {
    const llave = cadena !== undefined && numero !== undefined ? llaveTienda(cadena, numero) : null;
    if (llave !== tiendaDeSuplentes.current) {
      tiendaDeSuplentes.current = llave;
      setSuplentesTienda(null);
      setSuplenteDe(null);
    }
    if (cadena === undefined || numero === undefined) {
      pedidoSuplentes.current++;
      return;
    }
    void cargarSuplentes(cadena, numero);
  }, [cadena, numero, c.adopciones, cargarSuplentes]);

  const abrirSuplente = (f: FilaVista) => {
    // Nunca de una vista que ya no es la de la tienda elegida.
    if (!vista || desfasada) return;
    vistaDelDialogo.current = c.pedido.current;
    setSuplenteDe({ fila: f, chain: vista.tienda.chain, num: vista.tienda.num, tienda: vista.tienda.nombre, lunes: vista.semana.lunes });
  };

  /**
   * El programa de UNA persona cambió: se corrige en la copia local y en la del
   * servidor —no cuenta como «cambio sin guardar»—, sin recargar la vista. Si
   * la vista se recargó mientras el diálogo estaba abierto (un 409), y no hay
   * nada escrito, se pide otra vez: esa respuesta pudo leerse antes del cambio.
   */
  const cambiarSuplente = (key: string, s: SuplenteFila | null, datos?: SuplentesDeTienda) => {
    const t = suplenteDe;
    if (datos) {
      pedidoSuplentes.current++;
      setSuplentesTienda(datos);
    } else if (t) void cargarSuplentes(t.chain, t.num);
    if (!s || s.estado === 'propuesta') onCambioCatalogo?.();
    const v = c.vistaRef.current;
    if (!t || !v || v.tienda.chain !== t.chain || v.tienda.num !== t.num || v.semana.lunes !== t.lunes) return;
    c.ponerEnFila(key, { suplente: s });
    if (vistaDelDialogo.current !== c.pedido.current && !c.estaSucio()) void c.cargarVista();
  };

  const conflictoSuplente = () => {
    const t = suplenteDe;
    if (t) void cargarSuplentes(t.chain, t.num);
    if (!c.estaSucio()) void c.cargarVista();
    onCambioCatalogo?.();
  };

  // Se eligió otra tienda con el diálogo abierto: era de la que se deja.
  useEffect(() => {
    if (vistaDeOtraTienda) setSuplenteDe(null);
  }, [vistaDeOtraTienda]);

  // Proponer y certificar es de quien captura la TIENDA (no la semana). Desde el
  // 21-oct-2026 el respaldo es `permisos.suplentes`: `condicion` salió de la captura.
  const puedeCatalogo = suplentesTienda?.permisos.proponer ?? vista?.permisos.suplentes ?? false;

  // ------------------------------------------------------------ Excel
  const [subida, setSubida] = useState<{ archivo: File; lunes: string } | null>(null);
  const [excelOcupado, setExcelOcupado] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const menuExcel = useRef<TieredMenu>(null);
  const inputArchivo = useRef<HTMLInputElement>(null);

  // Una vista previa es de SU semana: si la semana cambia, el diálogo se cierra.
  useEffect(() => {
    setSubida(null);
  }, [lunes]);

  // Mientras se guarda, envía o valida un Excel, no se cambia de tienda ni de semana.
  useEffect(() => {
    onOcupado?.(trabajando !== null || excelOcupado);
  }, [trabajando, excelOcupado, onOcupado]);

  const bajarPlantilla = async (semana: string, tiendas: TiendaRef[]) => {
    setDescargando(true);
    try {
      guardarArchivo(await MetasColaboradorService.plantillaExcel(semana, tiendas));
    } catch (e) {
      onAviso('error', 'No se pudo bajar la plantilla', (e as Error).message);
    } finally {
      setDescargando(false);
    }
  };

  const bajarEsta = () => {
    if (!vista) return;
    void bajarPlantilla(vista.semana.lunes, [{ chain: vista.tienda.chain, num: vista.tienda.num }]);
  };

  const bajarTodas = () => {
    if (!lista) return;
    if (c.capturables.length > MAX_TIENDAS_EXCEL) {
      onAviso(
        'warn',
        `Son ${c.capturables.length} tiendas`,
        `El máximo por archivo es ${MAX_TIENDAS_EXCEL}: abre cada tienda y baja su plantilla.`,
      );
      return;
    }
    void bajarPlantilla(
      lista.semana.lunes,
      c.capturables.map((t) => ({ chain: t.chain, num: t.num })),
    );
  };

  const elegirArchivo = () => inputArchivo.current?.click();

  const subirExcel = () => {
    if (!c.estaSucio()) {
      elegirArchivo();
      return;
    }
    // El clic de «Sí» es el que abre el selector: el navegador sólo lo abre en
    // respuesta a un clic, así que no se puede guardar antes y luego abrirlo.
    confirmDialog({
      group: GRUPO_CONFIRMAR,
      header: 'Cambios sin guardar',
      message:
        'Subir un Excel cambia lo guardado y, al terminar, esta tienda se recarga: se pierde lo que escribiste y no guardaste. ¿Continuar?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, subir',
      rejectLabel: 'Seguir aquí',
      accept: elegirArchivo,
    });
  };

  const alElegirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0] ?? null;
    // Se limpia para que elegir OTRA VEZ el mismo archivo vuelva a disparar el cambio.
    e.target.value = '';
    if (!archivo) return;
    const semana = lunes;
    try {
      // Los bytes se leen AHORA: la vista previa y «Guardar» mandan lo mismo.
      setSubida({ archivo: await fijarArchivo(archivo), lunes: semana });
    } catch (err) {
      onAviso('error', 'No se pudo leer el archivo', (err as Error).message);
    }
  };

  const alAplicarExcel = (r: ResultadoImportacion) => {
    const fallidas = r.tiendas.filter((t) => t.errores.length > 0).length;
    onAviso(
      fallidas || !r.resumen.guardadas ? 'warn' : 'success',
      `Excel: ${r.resumen.guardadas} tienda(s) guardada(s)`,
      fallidas ? `${fallidas} no se guardaron por errores: revisa el detalle.` : undefined,
    );
    if (r.resumen.guardadas > 0) {
      void c.cargarLista();
      void c.cargarVista();
    }
  };

  // Por tienda (`puedeCapturar`, que ya trae la ventana de cada una) y no por
  // `semana.editable`: en la semana de gracia una tienda RECHAZADA sí se
  // captura y la API acepta su Excel, aunque la semana en general ya no se edite.
  const excelDeshabilitado = !lista || !c.capturables.length || trabajando !== null || excelOcupado;
  const semanaCerrada = !!lista && !c.capturables.length && !lista.semana.editable;
  const itemsExcel: MenuItem[] = [
    {
      label: 'Bajar plantilla de esta tienda',
      icon: 'pi pi-download',
      // Mientras carga OTRA tienda, «esta» sería la anterior.
      disabled: !vista || soloLectura || c.cargandoVista || sel !== llaveTienda(vista.tienda.chain, vista.tienda.num),
      command: bajarEsta,
    },
    ...(c.capturables.length > 1
      ? [
          {
            label: `Bajar plantilla de todas mis tiendas (${c.capturables.length})`,
            icon: 'pi pi-download',
            command: bajarTodas,
          },
        ]
      : []),
    { separator: true },
    { label: 'Subir Excel…', icon: 'pi pi-upload', command: subirExcel },
  ];

  // ------------------------------------------------------------ tienda
  /** Cambiar de tienda guarda antes lo pendiente; si no se puede, pregunta. */
  const elegirTienda = async (nueva: string | null) => {
    if (nueva === sel) return;
    if (!c.estaSucio()) {
      setSel(nueva);
      return;
    }
    if (await auto.guardarYa()) {
      setSel(nueva);
      return;
    }
    confirmDialog({
      group: GRUPO_CONFIRMAR,
      header: 'Cambios sin guardar',
      message: 'No se pudo guardar lo que escribiste. Si cambias de tienda se pierde. ¿Continuar?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, descartar',
      rejectLabel: 'Seguir aquí',
      accept: () => setSel(nueva),
    });
  };

  // «Ver captura completa» desde Autorización: se abre en cuanto la lista de
  // la semana está; si esa tienda no sale en el selector de esta cuenta, se dice.
  const elegirTiendaRef = useRef(elegirTienda);
  elegirTiendaRef.current = elegirTienda;
  const onAvisoRef = useRef(onAviso);
  onAvisoRef.current = onAviso;
  const [pedidaFuera, setPedidaFuera] = useState<TiendaParam | null>(null);
  useEffect(() => {
    if (abrirTienda) setPedidaFuera({ chain: abrirTienda.chain, num: abrirTienda.num });
  }, [abrirTienda]);
  const enSelector = c.enSelector;
  useEffect(() => {
    if (!pedidaFuera || !lista) return;
    setPedidaFuera(null);
    const p = pedidaFuera;
    const k = llaveTienda(p.chain, p.num);
    // La misma tienda que ya está abierta: se relee (Autorización pudo
    // rechazarla o reabrirla desde que se cargó), salvo que haya algo sin guardar.
    const abrir = () => {
      if (k === cRef.current.sel) {
        if (!cRef.current.estaSucio()) void cRef.current.cargarVista();
      } else void elegirTiendaRef.current(k);
    };
    if (enSelector.some((t) => llaveTienda(t.chain, t.num) === k)) {
      abrir();
      return;
    }
    // La lista puede ser de antes del cambio (un rechazo devuelve la tienda al
    // selector): se pide otra vez antes de decir que no está.
    void cRef.current.cargarLista().then((l) => {
      if (l && tiendasDelSelector(l).some((t) => llaveTienda(t.chain, t.num) === k)) abrir();
      else onAvisoRef.current('warn', MENSAJE_TIENDA_DEL_ENLACE, `${p.chain} ${p.num}`);
    });
  }, [pedidaFuera, lista, enSelector]);

  // ------------------------------------------------------------ chips por estado (21-oct-2026)
  // Diseño `Supervisor-revision`: la lista de tiendas por dónde está cada una.
  // Filtran el selector (la tienda abierta siempre se queda en él) y, si la
  // abierta no es del grupo, abren la primera que sí (guardando antes lo pendiente).
  // Por omisión, «Te la enviaron» si hay alguna (diseño `Supervisor-revision`:
  // el supervisor entra a lo que le toca revisar); si no, «Todas». Lo que elija
  // a mano se respeta hasta cambiar de semana (revisión de la fase B, 21-oct-2026).
  // Sin la captura de la tienda (22-oct-2026) los chips no nombran al gerente.
  const [filtroElegido, setFiltroElegido] = useState<FiltroCaptura | null>(null);
  useEffect(() => {
    setFiltroElegido(null);
  }, [lunes]);
  const conteos = useMemo(() => conteosCaptura(c.enSelector, capturaTienda), [c.enSelector, capturaTienda]);
  const filtroCaptura: FiltroCaptura = filtroElegido ?? (conteos.te_la_enviaron > 0 ? 'te_la_enviaron' : 'todas');
  const conChips = c.enSelector.length > 1;
  const filtrosVisibles = filtrosCaptura(capturaTienda).filter((f) => f.id === 'todas' || f.id === filtroCaptura || conteos[f.id] > 0);
  const elegirFiltro = (f: FiltroCaptura) => {
    setFiltroElegido(f);
    const del = c.enSelector.filter((t) => coincideFiltroCaptura(t, f, capturaTienda));
    if (del.length && !del.some((t) => llaveTienda(t.chain, t.num) === sel)) void elegirTienda(llaveTienda(del[0].chain, del[0].num));
  };
  const opcionesTienda: OpcionTienda[] = c.enSelector
    .filter((t) => coincideFiltroCaptura(t, filtroCaptura, capturaTienda) || llaveTienda(t.chain, t.num) === sel)
    .map((t) => ({
      valor: llaveTienda(t.chain, t.num),
      etiqueta: `${t.chain} ${t.num} · ${t.tienda}`,
      estado: t.estado,
      textoEstado: textoEstadoCaptura(t.estado, !!t.devuelta, capturaTienda),
    }));

  // ------------------------------------------------------------ copiar semana anterior
  const copiar = async () => {
    if (!c.vistaRef.current) return;
    setCopiando(true);
    try {
      // Lo pendiente se guarda ANTES de pedir la propuesta, y el autoguardado
      // queda apagado mientras llega: si no, el guardado propio subía la versión
      // a media copia y salía un falso «otra pestaña u otra persona».
      await auto.guardarYa();
      await c.esperarGuardado();
      const v = c.vistaRef.current;
      if (!v) return;
      const r = await MetasColaboradorService.copiarAnterior(v.semana.lunes, v.tienda.chain, v.tienda.num);
      const ahora = c.vistaRef.current;
      // Otra tienda u otra semana abierta mientras tanto: la propuesta no es de ésta.
      if (!ahora || ahora.tienda.chain !== v.tienda.chain || ahora.tienda.num !== v.tienda.num || ahora.semana.lunes !== v.semana.lunes)
        return;
      if (r.version !== ahora.estado.version) {
        c.setError({
          mensaje:
            'La captura de esta tienda cambió desde que la abriste (otra pestaña u otra persona). Recarga para copiar sobre lo último.',
          errores: [],
          duplicados: [],
          conflicto: true,
        });
        return;
      }
      if (!r.propuestas.length && !(r.marcas ?? []).length) {
        setResultadoCopia(r.omitidas.length ? { numero: r.anterior.numero, omitidas: r.omitidas, saltadas: [], respetadas: 0 } : null);
        onAviso(
          'warn',
          'Nada que copiar',
          r.anterior.estado === 'sin_captura'
            ? `La semana ${r.anterior.numero} no tiene captura en esta tienda.`
            : `Nadie de la semana ${r.anterior.numero} tiene meta o motivo que copiar aquí.`,
        );
        return;
      }
      const aplicar = (reemplazar: boolean) => {
        const res = aplicarCopia(c.filasRef.current, r.propuestas, { reemplazar, catalogo: alcance.catalogo, marcas: r.marcas });
        c.reemplazarFilas(res.filas);
        const hayOmitidas = r.omitidas.length + res.saltadas.length > 0;
        setResultadoCopia(
          hayOmitidas || res.respetadas
            ? { numero: r.anterior.numero, omitidas: r.omitidas, saltadas: res.saltadas, respetadas: res.respetadas }
            : null,
        );
        // Transporte y bodeguero fijo de la semana anterior (21-oct-2026): se dicen aparte.
        const marcadas = res.marcadas ? ` ${res.marcadas} con transporte o bodeguero fijo de la semana ${r.anterior.numero}.` : '';
        if (res.copiadas)
          onAviso(
            hayOmitidas ? 'warn' : 'success',
            'Semana anterior copiada',
            `${res.copiadas} colaborador(es) con lo de la semana ${r.anterior.numero}. Revísalo: se guarda solo.${
              res.respetadas ? ` ${res.respetadas} ya tenían meta o motivo y se respetaron.` : ''
            }${marcadas}`,
          );
        else if (res.marcadas)
          onAviso('success', 'Semana anterior copiada', `${marcadas.trim()} Revísalo: se guarda solo.`);
        else onAviso('warn', 'Nada que copiar', 'Todas las filas con propuesta ya tenían meta o motivo: se respetaron.');
      };
      const pisan = propuestasQuePisan(c.filasRef.current, r.propuestas);
      if (!pisan.length) {
        aplicar(false);
        return;
      }
      // Nunca se pisa lo escrito sin preguntar (SPEC §6 y criterio 10).
      confirmDialog({
        group: GRUPO_CONFIRMAR,
        header: 'Ya hay datos capturados',
        message: `${
          pisan.length === 1 ? '1 colaborador ya tiene' : `${pisan.length} colaboradores ya tienen`
        } meta o motivo esta semana y la semana ${r.anterior.numero} dice otra cosa (${listaDeNombres(
          pisan.map((p) => p.nombre),
        )}). ¿Reemplazar lo escrito o sólo llenar los vacíos?`,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Reemplazar lo escrito',
        rejectLabel: 'Sólo llenar los vacíos',
        accept: () => aplicar(true),
        reject: () => aplicar(false),
      });
    } catch (e) {
      c.setError(aErrorPantalla(e));
    } finally {
      setCopiando(false);
    }
  };

  const puedeCopiar = !!vista && !bloqueada && !plantillaIncompleta && trabajando === null && !copiando && !repartiendo;
  const botonCopiar = (clase?: string) => (
    <button type="button" className={cx(CLASE_BOTON, clase)} onClick={() => void copiar()} disabled={!puedeCopiar}>
      {copiando ? <Girando /> : <Icono d={ICONO_COPIAR} />}
      Copiar semana anterior
    </button>
  );

  // ------------------------------------------------------------ la Casa (ronda F, 23-oct-2026)
  // «+ Casa» junto a «Copiar semana anterior» (en el teléfono, al pie de la tarjeta
  // resumen). Sólo si la API anuncia la Casa y la cuenta la puede agregar.
  // Durante un guardado sí (como teclear): `adoptarConservando` respeta lo que cambió
  // mientras iba. Con el autoguardado cada 2 s, apagarlo hacía parpadear el botón.
  const puedeTocarCasa =
    !!vista && !bloqueada && !desfasada && !plantillaIncompleta && (trabajando === null || trabajando === 'guardar') && !copiando && !repartiendo;
  const botonCasa = (clase?: string) =>
    vista && !soloLectura && c.puedeAgregarCasa ? (
      <button
        type="button"
        className={cx(CLASE_BOTON, clase)}
        onClick={c.agregarCasa}
        disabled={!puedeTocarCasa}
        title={TITULO_AGREGAR_CASA}
        data-testid="agregar-casa"
      >
        + Casa
      </button>
    ) : null;
  /** «Quitar la Casa»: con meta, se pregunta (la suma de la tienda baja). Se guarda sola. */
  const quitarCasa = () => {
    const f = c.filasRef.current.find((x) => esFilaCasa(x));
    if (!f) return;
    if (f.metaMonto === null || f.metaMonto === undefined) {
      c.quitarCasa();
      return;
    }
    confirmDialog({
      group: GRUPO_CONFIRMAR,
      header: 'Quitar la Casa',
      message: `${f.nombre} tiene meta de ${pesosEnteros(f.metaMonto)}: al quitarla, la suma de la tienda baja esa cantidad. ¿Quitarla?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, quitar',
      rejectLabel: 'Cancelar',
      accept: () => c.quitarCasa(),
    });
  };

  // ------------------------------------------------------------ repartir (fase 6 y ronda F)
  /** La vista sigue siendo la de la tienda y semana que pidieron la propuesta (si no, no es de ésta). */
  const sigueAbierta = (v: { tienda: { chain: string; num: number }; semana: { lunes: string } }) => {
    const ahora = c.vistaRef.current;
    return ahora && ahora.tienda.chain === v.tienda.chain && ahora.tienda.num === v.tienda.num && ahora.semana.lunes === v.semana.lunes
      ? ahora
      : null;
  };
  const conflictoReparto = () =>
    c.setError({
      mensaje: 'La captura de esta tienda cambió desde que la abriste (otra pestaña u otra persona). Recarga para repartir sobre lo último.',
      errores: [],
      duplicados: [],
      conflicto: true,
    });

  /**
   * Lo pendiente se guarda ANTES de pedir una propuesta (la API reparte sobre lo
   * guardado), como en «Copiar semana anterior». `null` = no se pudo: ya se dijo por qué.
   */
  const guardarAntesDeRepartir = async () => {
    const guardado = await auto.guardarYa();
    await c.esperarGuardado();
    // F6-02: la API reparte sobre lo GUARDADO. Si lo escrito no se pudo guardar
    // (errores de celda, autoguardado detenido), la propuesta diría «Cuadra»
    // y, tras «Aplicar», la tienda quedaría por encima de su meta.
    if (!guardado || c.estaSucio()) {
      onAviso('warn', 'No se puede repartir', 'Guarda o corrige las celdas marcadas antes de repartir: se reparte sobre lo guardado.');
      return null;
    }
    return c.vistaRef.current;
  };

  /**
   * «Según venta promedio»: pide la propuesta a la API y la muestra en una
   * vista previa. El autoguardado queda apagado mientras llega; si la versión de
   * la API no es la de la pantalla, alguien guardó entre medias y no se muestra.
   */
  const pedirReparto = async () => {
    if (!c.vistaRef.current) return;
    setRepartiendo('promedio');
    try {
      const v = await guardarAntesDeRepartir();
      if (!v) return;
      const r = await MetasColaboradorService.repartir(v.semana.lunes, v.tienda.chain, v.tienda.num);
      const ahora = sigueAbierta(v);
      if (!ahora) return;
      if (r.version !== ahora.estado.version) {
        conflictoReparto();
        return;
      }
      if (esRepartoElegibles(r)) return; // No pasa: sin cuerpo la API siempre propone.
      setReparto({ propuesta: r, llave: `${v.tienda.chain}|${v.tienda.num}|${v.semana.lunes}`, modo: 'promedio' });
    } catch (e) {
      // 422: no aplica (sin meta de tienda, nada que repartir, nadie que reciba). Se dice por qué.
      if (e instanceof MetasApiError && e.status === 422) onAviso('warn', 'No se puede repartir', e.message);
      else c.setError(aErrorPantalla(e));
    } finally {
      setRepartiendo(null);
    }
  };

  /** Lo que dice la pantalla si la API no sabe repartir en partes iguales (una anterior ignora el cuerpo). */
  const sinPartesIguales = () =>
    onAviso('warn', 'No se puede repartir en partes iguales', 'Este entorno todavía no reparte en partes iguales.');

  /**
   * «Repartir en partes iguales» (ronda F, 23-oct-2026), PASO 1: guarda lo
   * pendiente y pide a la API quién puede recibir. Se abre la lista para elegir.
   */
  const pedirRepartoIguales = async () => {
    if (!c.vistaRef.current) return;
    setRepartiendo('iguales');
    try {
      const v = await guardarAntesDeRepartir();
      if (!v) return;
      const r = await MetasColaboradorService.repartir(v.semana.lunes, v.tienda.chain, v.tienda.num, { modo: 'iguales' });
      const ahora = sigueAbierta(v);
      if (!ahora) return;
      if (r.version !== ahora.estado.version) {
        conflictoReparto();
        return;
      }
      // Una API anterior no lee el cuerpo: responde «según venta promedio», sin `modo`.
      if (!esRepartoElegibles(r)) {
        sinPartesIguales();
        return;
      }
      setIguales({ datos: r, llave: `${v.tienda.chain}|${v.tienda.num}|${v.semana.lunes}`, marcadas: null, abierto: true, error: null });
    } catch (e) {
      if (e instanceof MetasApiError && e.status === 422) onAviso('warn', 'No se puede repartir', e.message);
      else c.setError(aErrorPantalla(e));
    } finally {
      setRepartiendo(null);
    }
  };

  /**
   * PASO 2: con los elegidos, la propuesta de la API (en múltiplos de $100, con
   * el residuo a quien más vende). Si no aplica (422: poco por asignar, alguien
   * ya no puede recibir), se dice EN la lista, que sigue abierta para cambiarla.
   */
  const verRepartoIguales = async (keys: string[]) => {
    const pedido = iguales;
    const v = c.vistaRef.current;
    if (!pedido || !v || pedido.llave !== llaveVista) return;
    // La lista tapa la tabla (es modal): no debería haber nada nuevo; si lo hay, no se pide.
    if (c.estaSucio()) {
      setIguales({ ...pedido, marcadas: keys, error: 'Hay cambios sin guardar: ciérralo, deja que se guarden y vuelve a repartir.' });
      return;
    }
    setRepartiendo('iguales');
    setIguales({ ...pedido, marcadas: keys, error: null });
    try {
      const r = await MetasColaboradorService.repartir(v.semana.lunes, v.tienda.chain, v.tienda.num, { modo: 'iguales', keys });
      const ahora = sigueAbierta(v);
      if (!ahora) return;
      if (r.version !== ahora.estado.version) {
        setIguales(null);
        conflictoReparto();
        return;
      }
      if (!esPropuestaIguales(r)) {
        setIguales(null);
        sinPartesIguales();
        return;
      }
      setIguales({ ...pedido, marcadas: keys, abierto: false, error: null });
      setReparto({ propuesta: r, llave: pedido.llave, modo: 'iguales' });
    } catch (e) {
      if (e instanceof MetasApiError && e.status === 422) setIguales({ ...pedido, marcadas: keys, abierto: true, error: e.message });
      else {
        setIguales(null);
        c.setError(aErrorPantalla(e));
      }
    } finally {
      setRepartiendo(null);
    }
  };

  /** «Cambiar selección»: de la propuesta de vuelta a la lista, con lo que se había marcado. */
  const cambiarSeleccion = () => {
    setReparto(null);
    setIguales((x) => (x ? { ...x, abierto: true, error: null } : x));
  };

  /** Cerrar la propuesta (o la lista) sin aplicar: no cambia nada. */
  const cerrarReparto = () => {
    setReparto(null);
    setIguales(null);
  };

  /** «Aplicar»: las metas propuestas a la pantalla; el autoguardado las guarda. */
  const aplicarRepartoEnPantalla = () => {
    const r = reparto;
    setReparto(null);
    setIguales(null);
    if (!r || r.llave !== llaveVista) return;
    const res = aplicarReparto(c.filasRef.current, r.propuesta);
    if (res.aplicadas) c.reemplazarFilas(res.filas);
    const saltadas = res.saltadas.length
      ? ` No se tocó a ${listaDeNombres(res.saltadas.map((s) => s.nombre))}: ${res.saltadas[0].mensaje}.`
      : '';
    if (res.aplicadas)
      onAviso(
        saltadas ? 'warn' : 'success',
        'Meta repartida',
        `${res.aplicadas} colaborador(es) con su parte${r.modo === 'iguales' ? ' igual' : ''} de ${pesos(r.propuesta.porAsignar)}. Revísalo: se guarda solo.${saltadas}`,
      );
    else onAviso('warn', 'Nada que repartir', `Las metas cambiaron desde la propuesta.${saltadas}`);
  };

  const porAsignarVivo = vista ? porAsignarDe(totales.suma, vista.metaTienda) : null;
  const ofrecerReparto = !!vista && !soloLectura && hayQueRepartir(porAsignarVivo);
  // F6-02: con errores de celda o el autoguardado detenido lo escrito no se puede
  // guardar, y se reparte sobre lo guardado. Uno a la vez: el otro enlace se apaga.
  const repartoDeshabilitado =
    bloqueada || desfasada || plantillaIncompleta || trabajando !== null || copiando || errores.size > 0 || !!c.detenido;
  const repartir = ofrecerReparto
    ? {
        onClick: () => void pedirReparto(),
        ocupado: repartiendo === 'promedio',
        deshabilitado: repartoDeshabilitado || repartiendo === 'iguales',
      }
    : undefined;
  // Sólo si la API lo anuncia (ronda F): con una anterior, el enlace no aparece.
  const repartirIguales =
    ofrecerReparto && vista?.capacidades?.repartirIguales === true
      ? {
          onClick: () => void pedirRepartoIguales(),
          ocupado: repartiendo === 'iguales',
          deshabilitado: repartoDeshabilitado || repartiendo === 'promedio',
        }
      : undefined;

  // ------------------------------------------------------------ enviar
  const enviar = async () => {
    auto.cancelar();
    await c.enviar();
  };

  // ------------------------------------------------------------ devolver al gerente (21-oct-2026)
  const [devolviendo, setDevolviendo] = useState<string | null>(null);
  const [errorDevolver, setErrorDevolver] = useState<string | null>(null);
  const abrirDevolver = () => {
    if (!vista) return;
    setErrorDevolver(null);
    setDevolviendo(`${vista.tienda.chain} ${vista.tienda.num} · ${vista.tienda.nombre}`);
  };
  const devolver = async (comentario: string): Promise<boolean> => {
    auto.cancelar();
    setErrorDevolver(null);
    return c.devolver(comentario, (e) => setErrorDevolver(aErrorPantalla(e).mensaje));
  };
  // Otra tienda u otra semana: el diálogo era de la que se deja.
  useEffect(() => {
    setDevolviendo(null);
  }, [llaveVista]);

  // ------------------------------------------------------------ pie
  const estadoVista = vista?.estado.estado;
  const guardadoTexto = (): React.ReactNode => {
    if (trabajando === 'guardar' || auto.guardando) return 'Guardando…';
    if (c.detenido) return <span className="font-semibold text-metas-aviso">{c.detenido.mensaje}</span>;
    if (sucio)
      return (
        <>
          <span>Cambios sin guardar</span>
          {c.puedeAutoguardar ? ' · se guardan solos en unos segundos' : ''}
        </>
      );
    if (vista?.estado.actualizadoEn && estadoVista === 'revision')
      return `Ajustes guardados ${c.ultimoGuardado?.auto ? 'automáticamente ' : ''}${momentoMx(vista.estado.actualizadoEn)}`;
    if (vista?.estado.actualizadoEn && (estadoVista === 'borrador' || estadoVista === 'rechazada'))
      return `Borrador guardado ${c.ultimoGuardado?.auto ? 'automáticamente ' : ''}${momentoMx(vista.estado.actualizadoEn)}`;
    if (estadoVista === 'sin_captura') return 'Todavía sin guardar: se guarda solo en cuanto escribas.';
    return null;
  };

  const mensajePie = (): React.ReactNode => {
    if (!porEnviar && (textoBodeguerosConMeta || textoCasaConMeta))
      // En la barra fija (lo que se ve al pulsar «Enviar»), bajo «Lista para enviar».
      return (
        <>
          <span className="block">Lista para enviar al regional</span>
          {textoBodeguerosConMeta && (
            <span className="block text-xs font-semibold text-metas-aviso [overflow-wrap:anywhere]" data-testid="barra-meta-bodeguero">
              {textoBodeguerosConMeta}
            </span>
          )}
          {textoCasaConMeta && (
            <span className="block text-xs font-semibold text-metas-aviso [overflow-wrap:anywhere]" data-testid="barra-meta-casa">
              {textoCasaConMeta}
            </span>
          )}
        </>
      );
    if (!porEnviar) return 'Lista para enviar al regional';
    const texto = esMovil && totales.pendientes > 0 && porEnviar === textoFaltan(totales.pendientes) ? textoFaltan(totales.pendientes, true) : porEnviar;
    return (
      <span data-testid="motivo-no-enviar" id="metas-motivo-no-enviar" className={esMovil ? 'text-[13px] leading-[18px]' : undefined}>
        {texto}
      </span>
    );
  };

  const deshabilitarGuardar = !c.necesitaGuardar || trabajando !== null || plantillaIncompleta || copiando || bloqueada;
  const deshabilitarEnviar = porEnviar !== null || trabajando !== null || plantillaIncompleta;

  // ------------------------------------------------------------ Excel (control)
  // En el teléfono, con una tienda abierta, va en la tarjeta resumen junto a
  // «Copiar semana anterior»: en su propio renglón bajo la tienda empujaba a los
  // colaboradores fuera de la primera pantalla (diseño `MovilCaptura`).
  const excelEnResumen = esMovil && !!vista;
  const controlExcel = (
    <div data-testid="control-excel" className={esMovil && !excelEnResumen ? 'ml-auto' : undefined}>
      <button
        type="button"
        className={CLASE_BOTON}
        onClick={(e) => menuExcel.current?.toggle(e)}
        disabled={excelDeshabilitado}
        aria-haspopup="menu"
        aria-controls="metas-menu-excel"
        title={semanaCerrada ? 'Esta semana ya no se puede modificar' : 'Bajar la plantilla de Excel o subirla llena'}
      >
        {descargando ? <Girando /> : <Icono d={ICONO_EXCEL} className="text-metas-exito" />}
        Excel
        <i className="pi pi-chevron-down text-xs" aria-hidden="true" />
      </button>
      {/* `TieredMenu` y no `Menu`: la app no tiene `PrimeReactProvider`, y el
          `Menu` emergente de PrimeReact 10 lee un contexto que no existe. */}
      <TieredMenu
        model={itemsExcel}
        popup
        ref={menuExcel}
        id="metas-menu-excel"
        className={CLASE_OVERLAY}
        style={{ width: 'auto', minWidth: '18rem', maxWidth: 'calc(100vw - 2rem)' }}
      />
      <input
        ref={inputArchivo}
        type="file"
        accept={ACEPTA_EXCEL}
        className="hidden"
        onChange={alElegirArchivo}
        data-testid="excel-archivo"
        aria-label="Archivo de Excel con las metas"
      />
    </div>
  );

  // ------------------------------------------------------------ vista
  // Pendientes de catálogo EN VIVO (fase 4): marca en la fila de cada persona.
  const pendientesDeFila = useMemo(() => pendientesPorFila(vista?.pendientesCatalogo), [vista]);
  const tabla = vista ? (
    <ColaboradoresTabla
      filas={filasVisibles}
      tienda={{ chain: vista.tienda.chain, num: vista.tienda.num }}
      motivos={alcance.motivos}
      motivosHistoricos={alcance.motivosHistoricos}
      // Mientras se pide la propuesta, nada se teclea: lo escrito entre medias no
      // estaría en lo guardado sobre lo que la API reparte (F6-02).
      soloLectura={bloqueada || !!repartiendo}
      errores={errores}
      onCambio={c.cambiar}
      catalogo={alcance.catalogo}
      diasApoyoMin={alcance.parametros.diasApoyoMin}
      referencia={vista.referencia}
      onSuplente={puedeCatalogo ? abrirSuplente : undefined}
      minimoCertificacion={minimoDeAlcance(alcance)}
      inactiva={desfasada}
      pendientesCatalogo={pendientesDeFila}
      conGerente={pasoPorGerente(vista.estado)}
      // «Quitar la Casa» (ronda F): sólo si la cuenta la puede quitar y nada está en camino.
      onQuitarCasa={c.puedeQuitarCasa && puedeTocarCasa ? quitarCasa : undefined}
    />
  ) : null;

  const erroresError = c.error;
  // «Devolver al gerente…» (fase B): sólo si la tienda captura. Apagada (22-oct-2026,
  // decisión del usuario), aunque la API lo permitiera, el gerente ya no la vería.
  const puedeDevolver = capturaTienda && !!vista?.permisos.devolver;
  const conPendientesEditables = !soloLectura && totales.pendientes > 0;
  // Cuántas metas cambió el supervisor frente a lo que puso el gerente (en vivo).
  const ajustes = vista && pasoPorGerente(vista.estado) ? filas.filter((f) => ajusteDelGerente(f, true) !== null).length : 0;

  return (
    <div className="flex flex-col gap-4" data-testid="captura-tab">
      {c.errorLista && (
        <div className="flex flex-wrap items-center gap-2 rounded-metas-control border border-metas-error bg-metas-error-fondo px-4 py-3 text-sm text-metas-error" role="alert">
          <span className="font-semibold">No se pudo leer la lista de tiendas: {c.errorLista}</span>
          <button type="button" className={CLASE_BOTON} onClick={() => void c.reintentarLista()}>
            Reintentar
          </button>
        </div>
      )}

      {conChips && (
        <div
          role="group"
          aria-label="Tiendas por estado"
          className={cx('flex gap-2', esMovil ? '-mx-4 overflow-x-auto px-4 pb-1 md:-mx-10 md:px-10' : 'flex-wrap')}
          data-testid="chips-captura"
        >
          {filtrosVisibles.map((f) => (
            <Chip
              key={f.id}
              activo={filtroCaptura === f.id}
              onClick={() => elegirFiltro(f.id)}
              contador={conteos[f.id]}
              disabled={trabajando !== null || excelOcupado}
            >
              {f.etiqueta}
            </Chip>
          ))}
        </div>
      )}

      {/* Barra de la tienda: selector, «Copiar semana anterior» y «Excel». */}
      <div className="flex flex-wrap items-end gap-3">
        <SelectorTienda
          opciones={opcionesTienda}
          value={sel}
          onChange={(v) => void elegirTienda(v)}
          // Mientras se guarda o envía, cambiar de tienda dejaba la respuesta sin dueño.
          disabled={trabajando !== null || excelOcupado}
          placeholder={lista ? (c.enSelector.length ? 'Elige una tienda' : 'No tienes tiendas que capturar') : 'Cargando tiendas…'}
          emptyMessage={filtroCaptura === 'todas' ? 'No tienes tiendas que capturar esta semana' : 'Ninguna tienda en ese estado'}
          className={esMovil ? 'w-full' : 'min-w-[280px] flex-grow md:max-w-[560px] md:flex-grow-0 md:basis-[380px] xl:basis-[520px]'}
        />
        <div className="hidden flex-grow md:block" />
        {!esMovil && vista && !soloLectura && botonCopiar()}
        {!esMovil && botonCasa()}
        {!excelEnResumen && controlExcel}
      </div>

      {resultadoCopia && (
        <div className={cx(CLASE_PANEL, 'flex flex-col gap-2 px-4 py-3 text-sm')} data-testid="copia-omitidas" role="status">
          <div className="flex items-start justify-between gap-3">
            <span className="font-semibold">
              Lo que no se copió de la semana {resultadoCopia.numero}
              {resultadoCopia.respetadas ? ` · ${resultadoCopia.respetadas} con meta o motivo se respetaron` : ''}
            </span>
            <button type="button" className={CLASE_BOTON_TEXTO} onClick={() => setResultadoCopia(null)} aria-label="Cerrar el aviso de la copia">
              Cerrar
            </button>
          </div>
          {resultadoCopia.omitidas.length + resultadoCopia.saltadas.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {[...resultadoCopia.omitidas, ...resultadoCopia.saltadas].map((o) => (
                <li key={`${o.key}-${o.mensaje}`}>
                  <b className="font-semibold">{o.nombre}</b>: {o.mensaje}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {c.cargandoVista && <div className="py-6 text-metas-texto-2">Cargando la plantilla de la tienda…</div>}

      {erroresError && (
        <div className="flex flex-col gap-2 rounded-metas-panel border border-metas-error bg-metas-error-fondo p-4" role="alert">
          <div className="font-semibold text-metas-error">{erroresError.mensaje}</div>
          {erroresError.duplicados.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-metas-error">
              {erroresError.duplicados.map((d) => (
                <li key={d.key}>
                  {d.nombre} ya tiene meta en {d.tienda}. Si no trabaja aquí, marca «Tienda errónea».
                </li>
              ))}
            </ul>
          )}
          {erroresError.errores.length > 0 && <AvisosLista avisos={erroresError.errores} />}
          {((erroresError.conflicto && erroresError.duplicados.length === 0) || (!vista && !!sel) || erroresError.recargar) && (
            <div>
              <button
                type="button"
                // Parte de renglón: a 390 px se salía de su recuadro rojo (`CLASE_BOTON` es `whitespace-nowrap`
                // y de alto fijo). Con `!`: entre clases de Tailwind gana la que va después en el CSS.
                className={cx(CLASE_BOTON, '!h-auto min-h-metas-control !whitespace-normal border-metas-error py-2 text-left !text-metas-error')}
                onClick={() => void c.cargarVista()}
              >
                <i className="pi pi-refresh" aria-hidden="true" />
                Recargar (se pierde lo que no guardaste)
              </button>
            </div>
          )}
        </div>
      )}

      {vista && (
        <>
          {estadoVista === 'rechazada' && (
            <div
              className="min-w-0 rounded-metas-panel border border-metas-error bg-metas-error-fondo px-4 py-3 text-sm text-metas-error [overflow-wrap:anywhere]"
              role="alert"
              data-testid="bloque-rechazo"
            >
              <p className="m-0">
                <strong className="font-semibold">
                  Rechazada por {vista.estado.resueltoPorNombre || vista.estado.resueltoPor || 'el regional'}
                  {vista.estado.resueltoEn ? ` el ${instanteMx(vista.estado.resueltoEn)}` : ''}:
                </strong>{' '}
                <span className="whitespace-pre-line">{vista.estado.comentario ? `«${vista.estado.comentario}»` : 'Sin comentario.'}</span>
              </p>
              <p className="m-0 mt-1">Corrige y vuelve a enviarla.</p>
            </div>
          )}

          {pasoPorGerente(vista.estado) && (
            // Quién y cuándo la mandó el gerente, con su nota (21-oct-2026). La
            // persona es la que eligió «Cambiar usuario» en el equipo: informativa.
            <div
              className={cx(
                'flex min-w-0 flex-col gap-1 rounded-metas-panel border px-4 py-3 text-sm [overflow-wrap:anywhere]',
                estadoVista === 'revision'
                  ? 'border-metas-banner-borde bg-metas-banner text-metas-banner-texto'
                  : 'border-metas-borde bg-metas-panel text-metas-texto-2',
              )}
              data-testid="enviada-por-gerente"
            >
              <p className="m-0">
                {/* Sólo «con el supervisor» lleva el título: en los demás estados el
                    renglón ya dice «Capturó … (gerente)» (no lo repite). */}
                {estadoVista === 'revision' && (
                  <>
                    <strong className="font-semibold">Te la envió el gerente</strong>
                    {' · '}
                  </>
                )}
                {vista.estado.enviadoGerenteNombre ? `Capturó ${nombrePropioSiMayusculas(vista.estado.enviadoGerenteNombre)} (gerente)` : 'Capturó el gerente'}
                {vista.estado.enviadoGerenteEn ? ` · ${instanteMx(vista.estado.enviadoGerenteEn)}` : ''}
                {vista.estado.enviadoGerenteCuenta ? ` · cuenta ${vista.estado.enviadoGerenteCuenta}` : ''}
              </p>
              {vista.estado.notaGerente && (
                <p className="m-0 whitespace-pre-line">Nota del gerente: «{vista.estado.notaGerente}»</p>
              )}
              {estadoVista === 'revision' && (
                <p className="m-0" data-testid="ajustes-supervisor">
                  {ajustes === 0
                    ? capturaTienda
                      ? 'Revísala: puedes ajustarla, devolvérsela con un comentario o enviarla al regional.'
                      : // Sin la captura de la tienda (22-oct-2026) no hay a quién devolvérsela.
                        'Revísala: puedes ajustarla y enviarla al regional.'
                    : `${ajustes === 1 ? '1 ajuste tuyo' : `${ajustes} ajustes tuyos`} a lo que puso el gerente: el regional lo verá.`}
                </p>
              )}
            </div>
          )}

          {/* Sólo con la captura de la tienda: apagada (22-oct-2026), el gerente ya no la ve
              y «te la vuelve a enviar» sería falso; la captura el supervisor. */}
          {capturaTienda && estadoVista === 'borrador' && vista.estado.devolucion && (
            <div
              className="min-w-0 rounded-metas-panel border border-metas-banner-borde bg-metas-banner px-4 py-3 text-sm text-metas-banner-texto [overflow-wrap:anywhere]"
              role="status"
              data-testid="bloque-devuelta"
            >
              <p className="m-0">
                <strong className="font-semibold">
                  Devuelta al gerente
                  {vista.estado.devolucion.porNombre ? ` por ${nombrePropioSiMayusculas(vista.estado.devolucion.porNombre)}` : ''}
                  {vista.estado.devolucion.en ? ` el ${instanteMx(vista.estado.devolucion.en)}` : ''}:
                </strong>{' '}
                <span className="whitespace-pre-line">
                  {vista.estado.devolucion.comentario ? `«${vista.estado.devolucion.comentario}»` : 'Sin comentario.'}
                </span>
              </p>
              <p className="m-0 mt-1">
                La ve en la app de la tienda y te la vuelve a enviar. Si hace falta, puedes capturarla tú y enviarla directo
                al regional.
              </p>
            </div>
          )}

          {plantillaIncompleta && (
            // La API responde 503 al guardar o enviar así: guardar una plantilla
            // del ERP metería gente de baja, y sin fichas se borrarían las altas.
            <div className="rounded-metas-panel border border-metas-error bg-metas-error-fondo px-4 py-3 text-sm font-semibold text-metas-error" role="alert">
              BUK no respondió: puedes revisar la tienda, pero no guardar ni enviar hasta que vuelva (la plantilla está incompleta). Intenta en unos minutos.
            </div>
          )}
          {!vista.semana.editable && !estadoSoloLectura && (
            <div className="rounded-metas-panel border border-metas-borde bg-metas-panel px-4 py-3 text-sm text-metas-texto-2">
              Esta semana ya no se puede modificar: sólo lectura.
            </div>
          )}
          {estadoSoloLectura ? (
            <div
              className={cx(
                'flex flex-col gap-2 rounded-metas-panel border px-4 py-3 text-sm [overflow-wrap:anywhere]',
                estadoVista === 'autorizada'
                  ? 'border-metas-exito bg-metas-exito-fondo text-metas-exito'
                  : 'border-metas-primario-suave bg-metas-info-fondo text-metas-info',
              )}
              data-testid="aviso-solo-lectura"
            >
              <p className="m-0 font-semibold">
                {`${motivoNoCapturar ?? MENSAJE_ENVIADA_SOLO_LECTURA}${
                  estadoVista === 'enviada' ? ' Si hay que corregir algo, el regional la rechaza con un comentario.' : ''
                }`}
              </p>
              {vista.estado.enviadoEn && (
                <p className="m-0 text-metas-texto-2">
                  Enviada por{' '}
                  <strong title={vista.estado.enviadoPor ?? undefined}>
                    {vista.estado.enviadoPorNombre || vista.estado.enviadoPor || '—'}
                  </strong>{' '}
                  el {instanteMx(vista.estado.enviadoEn)}
                  {estadoVista === 'autorizada' && vista.estado.resueltoEn && (
                    <>
                      {' '}
                      · Autorizada por{' '}
                      <strong title={vista.estado.resueltoPor ?? undefined}>
                        {vista.estado.resueltoPorNombre || vista.estado.resueltoPor || '—'}
                      </strong>{' '}
                      el {instanteMx(vista.estado.resueltoEn)}
                    </>
                  )}
                </p>
              )}
              {estadoVista === 'autorizada' && vista.estado.comentario && (
                <p className="m-0 text-metas-texto-2">Comentario del regional: «{vista.estado.comentario}»</p>
              )}
              {vista.permisos.reabrir && (
                <div>
                  <ReabrirControl
                    lunes={vista.semana.lunes}
                    chain={vista.tienda.chain}
                    num={vista.tienda.num}
                    version={vista.estado.version}
                    comentarioMin={alcance.parametros.comentarioMin}
                    onReabierta={() => {
                      onAviso('success', 'Tienda reabierta', 'Volvió a borrador: el supervisor la corrige y la reenvía.');
                      void c.cargarLista();
                      void c.cargarVista();
                      onCambioEstado?.();
                    }}
                    onError={(e) => c.setError(aErrorPantalla(e))}
                  />
                </div>
              )}
            </div>
          ) : (
            vista.semana.editable &&
            !vista.permisos.capturar && (
              <div
                className="rounded-metas-panel border border-metas-banner-borde bg-metas-banner px-4 py-3 text-sm text-metas-banner-texto"
                data-testid="motivo-no-capturar"
              >
                {motivoNoCapturar ?? 'Tu cuenta puede ver esta tienda, pero no capturarla.'}
              </div>
            )
          )}

          {!estadoSoloLectura && (
            <BannerPendientesCatalogo
              pendientes={vista.pendientesCatalogo}
              onVerPendientes={onVerPendientes ? () => onVerPendientes({ chain: vista.tienda.chain, num: vista.tienda.num }) : undefined}
              className={cx(desfasada && 'opacity-60')}
            />
          )}

          <ResumenCaptura
            vista={vista}
            totales={totales}
            tolerancia={alcance.parametros.toleranciaSuma}
            movil={esMovil}
            cargando={desfasada}
            repartir={repartir}
            repartirIguales={repartirIguales}
            conCasa={filas.some((f) => esFilaCasa(f))}
            capturaTienda={capturaTienda}
          >
            {esMovil && (
              <div className="flex flex-wrap gap-2">
                {!soloLectura && botonCopiar('min-w-0 flex-grow')}
                {botonCasa()}
                {controlExcel}
              </div>
            )}
          </ResumenCaptura>

          {esMovil ? (
            <>
              {!soloLectura && (
                <div aria-busy={desfasada || undefined} className={cx(desfasada && 'pointer-events-none opacity-60')} data-testid="banda-por-resolver">
                  <ChipsColaboradores filtro={filtro} onFiltro={alFiltrar} total={filas.length} pendientes={pendientesAhora.length} />
                </div>
              )}
              {tabla}
            </>
          ) : (
            <section className={CLASE_PANEL} aria-label="Metas de colaboradores">
              {conPendientesEditables || soloPendientes ? (
                // Mientras carga otra tienda, la banda es de la anterior: se atenúa
                // como el resumen y la tabla, y no se puede filtrar con ella.
                <div aria-busy={desfasada || undefined} className={cx(desfasada && 'pointer-events-none opacity-60')} data-testid="banda-por-resolver">
                  <BannerPorResolver
                    nombres={pendientesAhora.map((f) => f.nombre)}
                    filtro={filtro}
                    onFiltro={alFiltrar}
                    total={filas.length}
                    pendientes={pendientesAhora.length}
                  />
                </div>
              ) : null}
              {tabla}
            </section>
          )}

          <AvisosLista
            // Con la meta incompleta, el aviso dice cuántos días hay, como el recuadro (21-oct-2026).
            // La de la meta al bodeguero va aparte, en vivo (abajo): aquí se repetía. La de la Casa, igual (ronda F).
            avisos={avisosConMetaTienda(
              vista.validacion.advertencias.filter((a) => a.codigo !== CODIGO_META_A_BODEGUERO && a.codigo !== CODIGO_META_A_CASA),
              vista.metaTienda,
            )}
            titulo="Advertencias para el regional"
            nota={sucio ? 'Calculadas con lo último que se guardó: se recalculan al guardar.' : undefined}
          />

          {textoBodeguerosConMeta && (
            // Al final de la tienda, antes de la nota y de «Enviar» (23-oct-2026). Igual en el teléfono.
            <div
              role="status"
              className="flex max-w-[760px] items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              data-testid="aviso-meta-bodeguero"
            >
              <i className="pi pi-exclamation-triangle mt-0.5" aria-hidden="true" />
              <span className="flex min-w-0 flex-col gap-0.5 [overflow-wrap:anywhere]">
                <span className="font-semibold">
                  <span className="sr-only">Advertencia: </span>
                  {textoBodeguerosConMeta}
                </span>
                <span className="text-xs">
                  Es opcional y no impide enviar: el regional lo verá al autorizar. La meta de un bodeguero no se copia a la semana siguiente.
                </span>
              </span>
            </div>
          )}

          {textoCasaConMeta && (
            // La Casa (ronda F, 23-oct-2026), como el bodeguero: al final de la tienda, antes de la nota.
            <div
              role="status"
              className="flex max-w-[760px] items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              data-testid="aviso-meta-casa"
            >
              <i className="pi pi-exclamation-triangle mt-0.5" aria-hidden="true" />
              <span className="flex min-w-0 flex-col gap-0.5 [overflow-wrap:anywhere]">
                <span className="font-semibold">
                  <span className="sr-only">Advertencia: </span>
                  {textoCasaConMeta}
                </span>
                <span className="text-xs">
                  Cuenta para la meta de la tienda; nadie la cobra, ni el gerente. Es opcional y no impide enviar: el regional lo verá
                  al autorizar. La meta de la Casa no se copia a la semana siguiente.
                </span>
              </span>
            </div>
          )}

          {!soloLectura ? (
            <div className="flex max-w-[760px] flex-col gap-1.5">
              <label htmlFor="metas-nota" className="text-metas-label text-metas-texto-3">
                Nota para el regional <span className="font-normal text-metas-texto-2">(opcional)</span>
              </label>
              <InputTextarea
                id="metas-nota"
                value={c.nota}
                onChange={(e) => c.setNota(e.target.value)}
                disabled={bloqueada}
                rows={2}
                maxLength={1000}
                autoResize
                className="w-full"
              />
            </div>
          ) : (
            vista.estado.notaSupervisor && (
              <p className="m-0 text-sm text-metas-texto-2 [overflow-wrap:anywhere]">Nota del supervisor: «{vista.estado.notaSupervisor}»</p>
            )
          )}

          {!soloLectura && esMovil && puedeDevolver && (
            // En teléfono, «Devolver» va fuera de la barra fija (revisión de la fase B,
            // 21-oct-2026): con tres botones en la rejilla, «Enviar al regional» no cabía
            // (salía cortado a 360-414 px).
            <button
              type="button"
              className={cx(CLASE_BOTON, '!h-12 w-full border-metas-error-borde !text-metas-error')}
              onClick={abrirDevolver}
              disabled={trabajando !== null || c.enviando}
            >
              {trabajando === 'devolver' && <Girando />}
              Devolver al gerente…
            </button>
          )}

          {!soloLectura && (
            <BarraAccionesFija
              etiqueta="Guardar y enviar"
              tono={porEnviar ? 'aviso' : 'normal'}
              mensaje={mensajePie()}
              detalle={esMovil ? undefined : guardadoTexto()}
              className={cx('-mx-4 -mb-6 max-lg:!pb-4 md:-mx-10', desfasada && '[&>*]:opacity-60')}
              ocupada={desfasada}
            >
              {!esMovil && sucio && (
                <button
                  type="button"
                  className={CLASE_BOTON_TEXTO}
                  onClick={() => {
                    auto.cancelar();
                    c.descartar();
                  }}
                  disabled={trabajando !== null}
                >
                  Descartar cambios
                </button>
              )}
              {puedeDevolver && !esMovil && (
                // Sólo «con el supervisor» (21-oct-2026): vuelve al gerente con comentario.
                <button
                  type="button"
                  className={cx(CLASE_BOTON, 'border-metas-error-borde !text-metas-error')}
                  onClick={abrirDevolver}
                  disabled={trabajando !== null || c.enviando}
                >
                  {trabajando === 'devolver' && <Girando />}
                  Devolver al gerente…
                </button>
              )}
              <button
                type="button"
                className={cx(CLASE_BOTON, esMovil && '!h-12')}
                onClick={() => {
                  auto.cancelar();
                  void c.guardar();
                }}
                disabled={deshabilitarGuardar}
                aria-label={estadoVista === 'revision' ? 'Guardar ajustes' : 'Guardar borrador'}
              >
                {trabajando === 'guardar' ? <Girando /> : !esMovil && <Icono d={ICONO_GUARDAR} />}
                {esMovil ? 'Guardar' : estadoVista === 'revision' ? 'Guardar ajustes' : 'Guardar borrador'}
              </button>
              <button
                type="button"
                className={cx(CLASE_BOTON_PRIMARIO, esMovil && '!h-12')}
                onClick={() => void enviar()}
                disabled={deshabilitarEnviar}
                aria-describedby={porEnviar ? 'metas-motivo-no-enviar' : undefined}
              >
                {trabajando === 'enviar' ? <Girando /> : <Icono d={ICONO_ENVIAR} />}
                Enviar al regional
              </button>
            </BarraAccionesFija>
          )}
        </>
      )}

      <SuplenteDialog
        objetivo={vistaDeOtraTienda ? null : suplenteDe}
        suplentes={suplentesTienda}
        alcance={alcance}
        plantillaIncompleta={plantillaIncompleta}
        onHide={() => setSuplenteDe(null)}
        onCambio={cambiarSuplente}
        onConflicto={conflictoSuplente}
        onAviso={onAviso}
      />

      <DevolverDialog
        tienda={devolviendo}
        comentarioMin={alcance.parametros.comentarioMin}
        movil={esMovil}
        onHide={() => setDevolviendo(null)}
        onConfirmar={devolver}
        error={errorDevolver}
      />

      <ElegirRepartoDialog
        datos={iguales && iguales.abierto && iguales.llave === llaveVista ? iguales.datos : null}
        inicial={iguales?.marcadas ?? null}
        onVerReparto={(keys) => void verRepartoIguales(keys)}
        onHide={cerrarReparto}
        movil={esMovil}
        ocupado={repartiendo === 'iguales'}
        error={iguales?.error ?? null}
      />

      <RepartirDialog
        propuesta={reparto && reparto.llave === llaveVista ? reparto.propuesta : null}
        modo={reparto?.modo ?? 'promedio'}
        onAplicar={aplicarRepartoEnPantalla}
        onHide={cerrarReparto}
        onCambiarSeleccion={reparto?.modo === 'iguales' ? cambiarSeleccion : undefined}
        movil={esMovil}
      />

      <ImportarExcelDialog
        archivo={subida?.archivo ?? null}
        lunes={subida?.lunes ?? lunes}
        onHide={() => setSubida(null)}
        onAplicado={alAplicarExcel}
        onOcupado={setExcelOcupado}
        onIncierto={() => {
          void c.cargarLista();
          void c.cargarVista();
        }}
      />
    </div>
  );

};

export default CapturaTab;
